/**
 * Rollout rings (D40's "Rollout rings, per stage"; phase 7f): which servers get a new release first, and the rollout
 * record that opens the rings one by one.
 *
 * **Rings.** Three to begin with — `internal` (the staff and demo tenants, D39, and our own servers), `canary` (a few
 * customers we choose), `general` — an open enum, so more can follow. A tenant has a vendor-only ring (default
 * `general`); a **site** may override it (the smallest unit that shares servers). Both live in the stage's release
 * store beside the pins, never in anything a tenant's own users read:
 *
 *     rings/tenants/<tenantId>.json                   the tenant's ring
 *     rings/tenants/<tenantId>/sites/<siteId>.json    a site's override
 *
 * **The rollout.** Publishing a release starts one (`rollouts/current.json`, a copy per release under
 * `rollouts/<listHash>.json` for the history), written with S3's conditional writes. Each ring has a gate — how long it
 * soaks and how many completed productions its servers must report on the new release — that the scheduled evaluator
 * checks before it opens the next ring; any rejection halts the rollout.
 *
 * Stored documents (`dev/compat/storage.ts`): read with `parseStored` / `'preserving'` where a record is written back.
 */
import { z } from 'zod';
import { openEnum } from './openEnum.js';
import { isoTimestampWireSchema, sha256HexWireSchema, siteIdWireSchema, tenantIdWireSchema, boundedIdWireSchema } from './primitives.js';

/** The rings, in rollout order. */
export const RELEASE_RINGS = ['internal', 'canary', 'general'] as const;
/** A ring as a request names it: closed (a server validates it). */
export const releaseRingWireSchema = z.enum(RELEASE_RINGS);
/** A ring as a reader receives it: open (a newer writer may add rings). */
export const releaseRingOpenWireSchema = openEnum(RELEASE_RINGS);
export type ReleaseRingWire = z.infer<typeof releaseRingOpenWireSchema>;

/** A tenant with no ring assigned is in this one. */
export const DEFAULT_RELEASE_RING = 'general' satisfies ReleaseRingWire;

/** A ring assignment as stored (a tenant's, or a site's override). */
export const ringAssignmentWireSchema = z
  .object({
    ring: releaseRingOpenWireSchema,
    setAt: isoTimestampWireSchema,
    /** Why: "staff tenant", "canary customer since …". */
    note: z.string().max(512),
  })
  .strict();
export type RingAssignmentWire = z.infer<typeof ringAssignmentWireSchema>;

/**
 * `rolling`: rings open as their gates pass; `paused`: nothing more opens (a vendor's pause); `halted`: a rejection or
 * the vendor stopped it — servers already on the new release keep it, no other server gets it; `complete`: every ring
 * open and the last one's gate passed; `rolled-back`: every ring is served `previousListHash` again; `superseded`: a
 * publish replaced it before it was done (only ever in the history).
 */
export const ROLLOUT_STATUSES = ['rolling', 'paused', 'halted', 'complete', 'rolled-back', 'superseded'] as const;
const rolloutStatusWireSchema = openEnum(ROLLOUT_STATUSES);
export type RolloutStatusWire = z.infer<typeof rolloutStatusWireSchema>;

/** What a ring must show before the next one opens (or, for the last ring, before the rollout is complete). */
export const ringGateWireSchema = z
  .object({
    /** Minutes since the ring opened. */
    soakMinutes: z.number().int().min(0).max(60 * 24 * 60),
    /** `production.completed` reports on the new release from the ring's servers (one per server and release). */
    productionsCompleted: z.number().int().min(0).max(10_000),
  })
  .strict();
export type RingGateWire = z.infer<typeof ringGateWireSchema>;

export const rolloutRingWireSchema = z
  .object({
    ring: releaseRingOpenWireSchema,
    /** When the ring was opened (its servers are served the new release from then); null while it is closed. */
    openedAt: isoTimestampWireSchema.nullable(),
    gate: ringGateWireSchema,
  })
  .strict();
export type RolloutRingWire = z.infer<typeof rolloutRingWireSchema>;

/** A rollout, as stored (`rollouts/current.json`, `rollouts/<listHash>.json`). */
export const rolloutWireSchema = z
  .object({
    /** The release being rolled out. */
    listHash: sha256HexWireSchema,
    /** What a closed ring is served meanwhile (and every ring after a roll back); null when there was none before. */
    previousListHash: sha256HexWireSchema.nullable(),
    startedAt: isoTimestampWireSchema,
    /** Who published it. */
    startedBy: z.string().min(1).max(320),
    /** An urgent release (D40): every ring opens at once. */
    urgent: z.boolean(),
    status: rolloutStatusWireSchema,
    /** Why it was halted or rolled back; null otherwise. */
    haltReason: z.string().max(1000).nullable(),
    /** In rollout order. */
    rings: z.array(rolloutRingWireSchema).min(1).max(16),
    /** The last change (publish, a ring opened, a vendor action) and who made it (`rollout-gate`: the evaluator). */
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().min(1).max(320),
  })
  .strict();
export type RolloutWire = z.infer<typeof rolloutWireSchema>;

// ─── the vendor's routes ───

/** One ring of the current rollout, as the console shows its gate. */
export const ringProgressWireSchema = z
  .object({
    ring: releaseRingOpenWireSchema,
    openedAt: isoTimestampWireSchema.nullable(),
    gate: ringGateWireSchema,
    /** The ring's servers (active, enrolled on a site; cloud-production tasks are not in a ring). */
    servers: z.number().int().nonnegative(),
    /** Of them, how many report running the new release. */
    updated: z.number().int().nonnegative(),
    /** How many completed a production on it. */
    productionsCompleted: z.number().int().nonnegative(),
    /** Uncleared rejections of it (a supervisor's rollback) by the ring's servers. */
    rejections: z.number().int().nonnegative(),
    /** Critical alarms holding now on its updated servers. */
    criticalAlarms: z.number().int().nonnegative(),
    /** Minutes of soak left; null while the ring is closed. */
    soakRemainingMinutes: z.number().int().nonnegative().nullable(),
    /** The gate passes now. */
    passed: z.boolean(),
  })
  .strict();
export type RingProgressWire = z.infer<typeof ringProgressWireSchema>;

/** A server's uncleared rejection of the rollout's release. */
export const rolloutRejectionWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    siteId: siteIdWireSchema,
    nodeId: boundedIdWireSchema,
    label: z.string(),
    ring: releaseRingOpenWireSchema,
    /** A `RELEASE_REJECTION_REASONS` value, or one a newer node names. */
    reason: z.string(),
    at: isoTimestampWireSchema,
  })
  .strict();
export type RolloutRejectionWire = z.infer<typeof rolloutRejectionWireSchema>;

export const vendorRolloutResponseWireSchema = z
  .object({
    generatedAt: isoTimestampWireSchema,
    /** The current rollout, or null before the first publish that started one. */
    current: rolloutWireSchema.nullable(),
    /** Each of its rings' gates, in order; empty when there is none. */
    progress: z.array(ringProgressWireSchema),
    rejections: z.array(rolloutRejectionWireSchema),
    /** Earlier rollouts, newest first (the current one not repeated). */
    history: z.array(rolloutWireSchema),
  })
  .strict();
export type VendorRolloutResponseWire = z.infer<typeof vendorRolloutResponseWireSchema>;

export const ROLLOUT_ACTIONS = ['pause', 'resume', 'advance', 'halt', 'rollback'] as const;
export const rolloutActionWireSchema = z.enum(ROLLOUT_ACTIONS);
export type RolloutActionWire = z.infer<typeof rolloutActionWireSchema>;

/**
 * The statuses each action applies to — the rollout's state machine (D40), shared by the server that enforces it
 * (`@acp/releases/rollout.js`) and the console that offers only these. A status a newer release wrote (`unknown`)
 * allows nothing.
 */
export const ROLLOUT_ACTION_FROM: Readonly<Record<RolloutActionWire, readonly RolloutStatusWire[]>> = {
  pause: ['rolling'],
  resume: ['paused', 'halted'],
  advance: ['rolling', 'paused'],
  halt: ['rolling', 'paused'],
  rollback: ['rolling', 'paused', 'halted', 'complete'],
};

export const rolloutActionPathWireSchema = z.object({ action: rolloutActionWireSchema.describe('What to do with the current rollout.') }).strict();
export const rolloutActionRequestWireSchema = z
  .object({
    /** The rollout meant: refused (`conflict`) when the current one is another release's. */
    listHash: sha256HexWireSchema,
    /** Why (kept as a halt's or roll back's reason, and in the activity log). */
    reason: z.string().max(1000),
  })
  .strict();
export const rolloutResponseWireSchema = z.object({ rollout: rolloutWireSchema }).strict();

export const vendorRingSiteWireSchema = z
  .object({
    siteId: siteIdWireSchema,
    name: z.string(),
    /** The ring its servers are in: its override, else its tenant's. */
    ring: releaseRingOpenWireSchema,
    override: ringAssignmentWireSchema.nullable(),
  })
  .strict();

export const vendorRingTenantWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    name: z.string(),
    /** Its ring: the assigned one, else `general`. */
    ring: releaseRingOpenWireSchema,
    assigned: ringAssignmentWireSchema.nullable(),
    sites: z.array(vendorRingSiteWireSchema),
  })
  .strict();
export type VendorRingTenantWire = z.infer<typeof vendorRingTenantWireSchema>;

export const vendorRingsResponseWireSchema = z.object({ rings: z.array(releaseRingOpenWireSchema), tenants: z.array(vendorRingTenantWireSchema) }).strict();
export type VendorRingsResponseWire = z.infer<typeof vendorRingsResponseWireSchema>;

export const setRingRequestWireSchema = z.object({ ring: releaseRingWireSchema, note: z.string().max(512) }).strict();
export const ringResponseWireSchema = z.object({ assignment: ringAssignmentWireSchema.nullable() }).strict();
